import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ParentAuthLayout from "./ParentAuthLayout";
import {
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";
import { logParentSignupAccountCreated } from "../../utils/datadogLogger";
import {
  extractStudentEmail,
  extractStudentPreAuthToken,
  isAlreadyVerifiedResponse,
  isPendingStudentVerificationResponse,
  studentAuthApi,
} from "../../features/auth/authApi";
import { createStudentVerificationFlow } from "../../features/auth/studentOtpFlow";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getFormDataValue = (payload, key) => {
  if (!payload || typeof payload.get !== "function") return "";
  return String(payload.get(key) || "").trim();
};

const getParentSignupDisplayName = (responseData = {}, email = "") => {
  const responseName = [
    responseData?.name,
    responseData?.user_name,
    responseData?.full_name,
    responseData?.user?.name,
    responseData?.user?.user_name,
    responseData?.data?.name,
    responseData?.data?.user_name,
    responseData?.data?.user?.name,
    responseData?.data?.user?.user_name,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean);

  if (responseName) return responseName;

  const emailName = String(email || "").split("@")[0].trim();
  return emailName || "Parent";
};

const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return hasMinLength && hasLetter && hasNumber && hasSpecialChar;
};

const GENERIC_PARENT_SIGNUP_ERROR = "Unable to complete the request right now.";
const GENERIC_VERIFICATION_SENT_MESSAGE = "Verification code sent to your email.";

const ParentSignupPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    persistParentPostAuthRedirect(
      location.state?.redirectTo || readParentPostAuthRedirect()
    );
  }, [location.state?.redirectTo]);

  const signupMutation = useMutation({
    mutationFn: (payload) => studentAuthApi.signup(payload),
    onSuccess: async (response, variables) => {
      const email =
        extractStudentEmail(response) ||
        getFormDataValue(variables, "email") ||
        formData.email.trim().toLowerCase();
      logParentSignupAccountCreated({
        email,
        name: getParentSignupDisplayName(response?.data, email),
      });

      createStudentVerificationFlow({
        email,
        preAuthToken: extractStudentPreAuthToken(response),
        verificationType: response?.data?.data?.verification_type || "otp",
        accountType: "student",
        source: "parent_signup",
      });

      navigate("/parent/verification", {
        replace: true,
        state: {
          email,
          source: "parent_signup",
          successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
        },
      });
    },
    onError: async (error, variables) => {
      const errorData = error?.response?.data || {};

      if (isAlreadyVerifiedResponse(errorData)) {
        setErrors({
          submit: "This account is already verified.",
        });
        return;
      }

      if (isPendingStudentVerificationResponse(errorData)) {
        const email =
          getFormDataValue(variables, "email") || formData.email.trim().toLowerCase();
        const resendResponse = await studentAuthApi.resendSignupOtp({
          email,
        });

        createStudentVerificationFlow({
          email: extractStudentEmail(resendResponse) || email,
          preAuthToken: extractStudentPreAuthToken(resendResponse),
          verificationType:
            resendResponse?.data?.data?.verification_type || "otp",
          accountType: "student",
          source: "parent_signup",
        });

        navigate("/parent/verification", {
          replace: true,
          state: {
            email,
            source: "parent_signup",
            successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
          },
        });
        return;
      }

      setErrors({
        submit: GENERIC_PARENT_SIGNUP_ERROR,
      });
    },
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
    setErrors((current) => ({ ...current, [name]: "", submit: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!formData.email) {
      nextErrors.email = "Email is required.";
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    } else if (!validatePassword(formData.password)) {
      nextErrors.password =
        "Use 8+ characters with letters, numbers and a special character.";
    }

    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
    };
    signupMutation.mutate(payload);
  };

  const inputClassName =
    "w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3 text-base text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white";

  return (
    <ParentAuthLayout
      eyebrow="Parent Portal"
      accentLabel="Parent Portal"
      title="Create a Parent Account"
      description="Create your account to manage bookings, view history, and support your child’s learning journey."
      accent="Everything parents need to get started."
      accentDescription="Set up your account once to choose a plan, book sessions with ease, and keep your child’s learning support on track."
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#08059B]">
            Signup
          </p>
          <h2 className="mt-2 text-3xl font-bold text-[#111827]">
            Account Setup
          </h2>
        </div>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="parent-signup-email"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Email
          </label>
          <input
            id="parent-signup-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className={inputClassName}
            placeholder="student@email.com"
            autoComplete="email"
          />
          {errors.email ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-signup-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="parent-signup-password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="Choose a secure password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.password}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-signup-confirm-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Confirm password
          </label>
          <div className="relative">
            <input
              id="parent-signup-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() =>
                setShowConfirmPassword((current) => !current)
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.confirmPassword}
            </p>
          ) : null}
        </div>

        {errors.submit ? (
          <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {errors.submit}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={signupMutation.isPending}
          className="inline-flex w-full items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {signupMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              Creating account...
            </span>
          ) : (
            "Create Parent Account"
          )}
        </button>
      </form>

      <div className="mt-8 text-center text-sm leading-7 text-[#475569]">
        <Link
          className="text-lg font-bold text-[#08059B] underline sm:text-xl"
          to="/parent/login"
        >
          Login as a Parent
        </Link>
      </div>
    </ParentAuthLayout>
  );
};

export default ParentSignupPage;
